import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Check, ChevronRight, Download, ExternalLink, Eye, EyeOff, RefreshCw,
  Search, Trash2, X,
} from "lucide-react";

import { API_ENDPOINTS } from "../../../../constants/common";
import { apiClient } from "../../../../utils";

type ErrorEvent = {
  id: string;
  request_id: string;
  status_code: number;
  error_code: string;
  error_message: string;
  error_stack?: string | null;
  method: string;
  path: string;
  listing_type: string | null;
  user_id: string | null;
  user_email: string | null;
  authenticated: boolean;
  request_context: Record<string, unknown>;
  service_version: string;
  build_commit: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  review_status: "new" | "reviewed";
  occurred_at: string;
};

type ReviewTab = "all" | "new" | "reviewed";
type ReviewCounts = Record<ReviewTab, number>;

type Filters = {
  limit: string;
  user_id: string;
  user_email: string;
  status_code: string;
  error_code: string;
  error_category: string;
  listing_type: string;
  request_id: string;
  method: string;
  authenticated: string;
  from: string;
  to: string;
};

type Confirmation =
  | { type: "delete-one"; id: string }
  | { type: "delete-selected" }
  | { type: "clear-all" };

const EMPTY_FILTERS: Filters = {
  limit: "50", user_id: "", user_email: "", status_code: "", error_code: "",
  error_category: "", listing_type: "", request_id: "", method: "", authenticated: "",
  from: "", to: "",
};

const EMPTY_REVIEW_COUNTS: ReviewCounts = { all: 0, new: 0, reviewed: 0 };

const CATEGORIES = ["validation", "authorization", "not_found", "request_size", "rate_limit", "timeout", "database", "upstream", "internal", "request"];
const METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE", "SYSTEM"];
const MAX_SELECTION = 100;

const formatDate = (value: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat("ru-RU", {
    dateStyle: "short", timeStyle: "medium",
  }).format(date);
};

const statusClass = (status: number) => status >= 500
  ? "border-rose-200 bg-rose-50 text-rose-700"
  : status === 429
    ? "border-amber-200 bg-amber-50 text-amber-700"
    : "border-slate-200 bg-slate-50 text-slate-700";

const normalizeError = (error: any) => {
  const status = error?.response?.status;
  if (status === 401) return "Требуется авторизация менеджера.";
  if (status === 403) return "Этот раздел доступен только менеджерам.";
  if (status === 404) return "Событие больше не найдено.";
  if (status === 400) return error?.response?.data?.error || "Проверьте значения фильтров или действие.";
  return error?.response?.data?.error || error?.message || "Не удалось выполнить запрос.";
};

const toIso = (value: string) => value ? new Date(value).toISOString() : "";

const getFilename = (header?: string) => {
  const match = header?.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
  return match?.[1] ? decodeURIComponent(match[1]) : "error-events.csv";
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label className="block text-xs font-medium text-slate-600">
    <span className="mb-1.5 block">{label}</span>
    {React.isValidElement(children) ? React.cloneElement(children as React.ReactElement<any>, {
      className: "block w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#b34f86] focus:ring-2 focus:ring-[#efd5e3]",
    }) : children}
  </label>
);

const Info = ({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) => (
  <div>
    <p className="m-0 text-xs font-medium text-slate-500">{label}</p>
    <p className={`m-0 mt-1 break-all text-slate-900 ${mono ? "font-mono text-xs" : ""}`}>{value}</p>
  </div>
);

const JsonBlock = ({ title, value }: { title: string; value: unknown }) => (
  <section>
    <h4 className="m-0 text-sm font-semibold text-slate-950">{title}</h4>
    <pre className="mt-3 max-h-[360px] overflow-auto rounded-xl bg-slate-950 p-4 text-xs leading-5 text-slate-100">
      {typeof value === "string" ? value : JSON.stringify(value || {}, null, 2)}
    </pre>
  </section>
);

const EventDetail = ({ event }: { event: ErrorEvent }) => (
  <div className="space-y-7 text-sm">
    <section>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold tabular-nums ${statusClass(event.status_code)}`}>{event.status_code}</span>
        <span className="font-mono text-xs font-semibold text-slate-700">{event.error_code}</span>
        <ReviewBadge event={event} />
      </div>
      <p className="mb-0 mt-3 text-base font-medium leading-6 text-slate-900">{event.error_message}</p>
    </section>
    <section className="grid grid-cols-1 gap-x-8 gap-y-4 border-y border-slate-200 py-5 sm:grid-cols-2">
      <Info label="Время" value={formatDate(event.occurred_at)} />
      <Info label="Маршрут" value={`${event.method} ${event.path}`} mono />
      <Info label="Request ID" value={event.request_id} mono />
      <Info label="Тип объявления" value={event.listing_type || "—"} />
      <Info label="Аутентификация" value={event.authenticated ? "Да" : "Нет"} />
      <Info label="Версия" value={`${event.service_version} · ${event.build_commit}`} mono />
      <Info label="Просмотрено" value={event.reviewed_at ? `${formatDate(event.reviewed_at)} · менеджер ${event.reviewed_by || "—"}` : "Нет"} />
      <div>
        <p className="m-0 text-xs font-medium text-slate-500">Пользователь</p>
        {event.user_id ? <Link className="mt-1 inline-flex items-center gap-1 font-medium text-[#8b2a5e] hover:text-[#6e2048]" to={`/control-panel/users/${encodeURIComponent(event.user_id)}`}><span>ID {event.user_id}</span><ExternalLink size={14} /></Link> : <p className="m-0 mt-1 text-slate-900">—</p>}
        {event.user_email ? <p className="m-0 mt-1 break-all text-xs text-slate-500">{event.user_email}</p> : null}
      </div>
    </section>
    <JsonBlock title="Безопасный диагностический контекст" value={event.request_context} />
    {event.error_stack ? <JsonBlock title="Stack trace" value={event.error_stack} /> : null}
  </div>
);

const ReviewBadge = ({ event }: { event: ErrorEvent }) => event.review_status === "reviewed" ? (
  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700"><Check size={13} />Просмотрено</span>
) : <span className="inline-flex rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-xs font-semibold text-violet-700">Новое</span>;

const DetailPanel = ({ event, loading, error, pending, onClose, onReview, onDelete }: {
  event: ErrorEvent | null; loading: boolean; error: string | null; pending: boolean;
  onClose: () => void; onReview: (reviewed: boolean) => void; onDelete: () => void;
}) => (
  <aside className="fixed inset-0 z-50 flex justify-end bg-slate-950/20 p-0 sm:p-4" role="dialog" aria-modal="true" aria-label="Карточка события ошибки">
    <button type="button" className="absolute inset-0 cursor-default" aria-label="Закрыть карточку" onClick={onClose} />
    <div className="relative flex h-full w-full max-w-2xl flex-col overflow-hidden bg-white shadow-2xl sm:rounded-2xl">
      <header className="flex items-start justify-between border-b border-slate-200 px-5 py-5 sm:px-7">
        <div><p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-[#a34a7a]">Событие ошибки</p><h3 className="m-0 mt-1 text-xl font-semibold tracking-[-0.025em] text-slate-950">{event ? `#${event.id}` : "Загрузка"}</h3></div>
        <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-950" aria-label="Закрыть"><X size={20} /></button>
      </header>
      {event ? <div className="flex flex-wrap gap-2 border-b border-slate-200 px-5 py-3 sm:px-7">
        <button type="button" disabled={pending} onClick={() => onReview(event.review_status !== "reviewed")} className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-[#8b2a5e] hover:bg-[#fff5fa] disabled:opacity-50">{event.review_status === "reviewed" ? <EyeOff size={16} /> : <Eye size={16} />}{event.review_status === "reviewed" ? "Сделать новым" : "Отметить просмотренным"}</button>
        <button type="button" disabled={pending} onClick={onDelete} className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"><Trash2 size={16} />Удалить</button>
      </div> : null}
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6 sm:px-7">
        {loading ? <p className="text-sm text-slate-500">Загружаем полный безопасный контекст…</p> : null}
        {error ? <p className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{error}</p> : null}
        {event && !loading ? <EventDetail event={event} /> : null}
      </div>
    </div>
  </aside>
);

const ConfirmationDialog = ({ confirmation, selectedCount, pending, onCancel, onConfirm }: {
  confirmation: Confirmation; selectedCount: number; pending: boolean; onCancel: () => void; onConfirm: () => void;
}) => {
  const [phrase, setPhrase] = useState("");
  const clearingAll = confirmation.type === "clear-all";
  const title = clearingAll ? "Полностью очистить журнал?" : confirmation.type === "delete-one" ? "Удалить событие?" : `Удалить выбранные (${selectedCount})?`;
  const description = clearingAll ? "Это безвозвратно удалит все ошибки из журнала. Для продолжения введите фразу подтверждения." : "Удалённые события нельзя восстановить.";
  const enabled = !pending && (!clearingAll || phrase === "DELETE_ALL_ERROR_EVENTS");
  return <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/35 p-4" role="dialog" aria-modal="true" aria-label={title}>
    <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl sm:p-6">
      <h3 className="m-0 text-lg font-semibold text-slate-950">{title}</h3>
      <p className="mb-0 mt-2 text-sm leading-5 text-slate-600">{description}</p>
      {clearingAll ? <label className="mt-5 block text-xs font-semibold text-slate-600">Введите <span className="font-mono text-slate-900">DELETE_ALL_ERROR_EVENTS</span><input autoFocus value={phrase} onChange={(event) => setPhrase(event.target.value)} className="mt-2 block w-full rounded-lg border border-slate-200 px-3 py-2.5 font-mono text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100" /></label> : null}
      <div className="mt-6 flex justify-end gap-2"><button type="button" disabled={pending} onClick={onCancel} className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">Отмена</button><button type="button" disabled={!enabled} onClick={onConfirm} className="rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-40">{pending ? "Удаляем…" : "Удалить"}</button></div>
    </div>
  </div>;
};

export const ErrorEventsPanel = () => {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(EMPTY_FILTERS);
  const [reviewTab, setReviewTab] = useState<ReviewTab>("all");
  const [reviewCounts, setReviewCounts] = useState<ReviewCounts>(EMPTY_REVIEW_COUNTS);
  const [events, setEvents] = useState<ErrorEvent[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [actionPending, setActionPending] = useState(false);
  const [exportPending, setExportPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<ErrorEvent | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);

  const activeFiltersCount = useMemo(() => Object.entries(appliedFilters).filter(([key, value]) => key !== "limit" && value).length, [appliedFilters]);
  const selected = useMemo(() => Array.from(selectedIds), [selectedIds]);
  const allVisibleSelected = events.length > 0 && events.every((item) => selectedIds.has(item.id));

  const validateFilters = (next: Filters) => {
    const userId = Number(next.user_id);
    const status = Number(next.status_code);
    const limit = Number(next.limit);
    if (!Number.isInteger(limit) || limit < 1 || limit > 100) return "Размер страницы должен быть от 1 до 100.";
    if (next.user_id && (!Number.isInteger(userId) || userId < 1)) return "ID пользователя должен быть положительным целым числом.";
    if (next.user_email && (!/^\S+@\S+\.\S+$/.test(next.user_email) || next.user_email.length > 255)) return "Введите корректный e-mail пользователя (до 255 символов).";
    if (next.user_id && next.user_email) return "Используйте ID пользователя или e-mail, но не оба фильтра одновременно.";
    if (next.status_code && (!Number.isInteger(status) || status < 400 || status > 599)) return "HTTP-статус должен быть в диапазоне 400–599.";
    if (next.from && next.to && new Date(next.from) > new Date(next.to)) return "Начало периода не может быть позже окончания.";
    return null;
  };

  const buildParams = (next: Filters, includePagination: boolean, tab: ReviewTab = "all") => {
    const params: Record<string, string | number | boolean> = {};
    (Object.entries(next) as Array<[keyof Filters, string]>).forEach(([key, value]) => {
      if (!value || key === "limit") return;
      if (key === "authenticated") params[key] = value === "true";
      else if (key === "from" || key === "to") params[key] = toIso(value);
      else params[key] = value;
    });
    if (tab !== "all") params.reviewed = tab === "reviewed";
    if (includePagination) params.limit = Number(next.limit);
    return params;
  };

  const loadEvents = useCallback(async (cursor: string | null, next: Filters, append = false) => {
    const validationError = validateFilters(next);
    if (validationError) { setError(validationError); return; }
    append ? setLoadingMore(true) : setLoading(true);
    setError(null);
    try {
      const params = buildParams(next, true, reviewTab);
      if (cursor) params.cursor = cursor;
      const response = await apiClient.get(API_ENDPOINTS.classifiedsErrorEvents, { params });
      const data = Array.isArray(response.data?.data) ? response.data.data : [];
      setEvents((current) => append ? [...current, ...data] : data);
      setNextCursor(response.data?.meta?.pagination?.next_cursor || null);
      const counts = response.data?.meta?.review_counts;
      if (counts) setReviewCounts({ all: Number(counts.all) || 0, new: Number(counts.new) || 0, reviewed: Number(counts.reviewed) || 0 });
      if (!append) setSelectedIds(new Set());
    } catch (requestError) { setError(normalizeError(requestError)); }
    finally { setLoading(false); setLoadingMore(false); }
  }, [reviewTab]);

  useEffect(() => { void loadEvents(null, appliedFilters); }, [appliedFilters, reviewTab, loadEvents]);

  useEffect(() => {
    if (!selectedId) return;
    let active = true;
    setDetailLoading(true); setDetailError(null); setSelectedEvent(null);
    apiClient.get(API_ENDPOINTS.classifiedsErrorEvent(selectedId))
      .then((response) => { if (active) setSelectedEvent(response.data?.data || null); })
      .catch((requestError) => { if (active) setDetailError(normalizeError(requestError)); })
      .finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; };
  }, [selectedId]);

  const applyFilters = (event: React.FormEvent) => { event.preventDefault(); setAppliedFilters({ ...filters }); };
  const resetFilters = () => { setFilters(EMPTY_FILTERS); setAppliedFilters(EMPTY_FILTERS); };
  const updateFilter = (key: keyof Filters, value: string) => setFilters((current) => ({ ...current, [key]: value }));
  const toggleSelected = (id: string) => setSelectedIds((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else if (next.size < MAX_SELECTION) next.add(id);
    else setError(`Для массового действия можно выбрать не больше ${MAX_SELECTION} событий.`);
    return next;
  });
  const toggleVisible = () => setSelectedIds((current) => {
    if (allVisibleSelected) return new Set();
    const next = new Set<string>();
    events.slice(0, MAX_SELECTION).forEach((item) => next.add(item.id));
    if (events.length > MAX_SELECTION) setError(`Выбраны первые ${MAX_SELECTION} событий — это лимит массовой операции.`);
    return next;
  });

  const updateReview = async (ids: string[], reviewed: boolean) => {
    if (!ids.length) return;
    setActionPending(true); setError(null);
    try {
      const responses = await Promise.all(ids.map((id) => apiClient.patch(API_ENDPOINTS.classifiedsErrorEvent(id), { reviewed })));
      setSelectedIds(new Set());
      if (selectedId && ids.includes(selectedId)) {
        const response = responses[ids.indexOf(selectedId)];
        setSelectedEvent(response?.data?.data || null);
      }
      await loadEvents(null, appliedFilters);
    } catch (requestError) { setError(normalizeError(requestError)); }
    finally { setActionPending(false); }
  };
  const deleteOne = async (id: string) => {
    setActionPending(true); setError(null);
    try {
      await apiClient.delete(API_ENDPOINTS.classifiedsErrorEvent(id));
      setSelectedId(null); setConfirmation(null); setSelectedIds((current) => { const next = new Set(current); next.delete(id); return next; });
      await loadEvents(null, appliedFilters);
    } catch (requestError) { setError(normalizeError(requestError)); }
    finally { setActionPending(false); }
  };
  const confirmDeletion = async () => {
    if (!confirmation) return;
    if (confirmation.type === "delete-one") { await deleteOne(confirmation.id); return; }
    setActionPending(true); setError(null);
    try {
      const payload = confirmation.type === "clear-all" ? { confirm: "DELETE_ALL_ERROR_EVENTS" } : { ids: selected.map(Number) };
      await apiClient.delete(API_ENDPOINTS.classifiedsErrorEvents, { data: payload });
      setConfirmation(null); setSelectedIds(new Set()); setSelectedId(null);
      await loadEvents(null, appliedFilters);
    } catch (requestError) { setError(normalizeError(requestError)); }
    finally { setActionPending(false); }
  };
  const exportCsv = async () => {
    if (!appliedFilters.from || !appliedFilters.to) { setError("Для CSV-экспорта укажите начало и конец периода и примените фильтры."); return; }
    setExportPending(true); setError(null);
    try {
      const response = await apiClient.get(API_ENDPOINTS.classifiedsErrorEventsExport, { params: buildParams(appliedFilters, false, reviewTab), responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url; link.download = getFilename(response.headers?.["content-disposition"]); link.click();
      window.URL.revokeObjectURL(url);
    } catch (requestError) { setError(normalizeError(requestError)); }
    finally { setExportPending(false); }
  };

  return <div className="px-4 sm:px-0">
    <form onSubmit={applyFilters} className="border-y border-slate-200 bg-white py-4 sm:rounded-2xl sm:border sm:px-5 sm:py-5">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Событий на странице"><select value={filters.limit} onChange={(event) => updateFilter("limit", event.target.value)}>{[25, 50, 100].map((item) => <option key={item} value={item}>{item}</option>)}</select></Field>
        <Field label="ID пользователя"><input value={filters.user_id} onChange={(event) => updateFilter("user_id", event.target.value)} inputMode="numeric" placeholder="Например, 755…" /></Field>
        <Field label="Email пользователя"><input value={filters.user_email} onChange={(event) => updateFilter("user_email", event.target.value)} type="email" placeholder="user@example.com" /></Field>
        <Field label="HTTP-статус"><input value={filters.status_code} onChange={(event) => updateFilter("status_code", event.target.value)} inputMode="numeric" placeholder="400–599" /></Field>
        <Field label="Код ошибки"><input value={filters.error_code} onChange={(event) => updateFilter("error_code", event.target.value)} placeholder="VALIDATION…" /></Field>
        <Field label="Категория"><select value={filters.error_category} onChange={(event) => updateFilter("error_category", event.target.value)}><option value="">Все категории</option>{CATEGORIES.map((item) => <option key={item} value={item}>{item}</option>)}</select></Field>
        <Field label="Тип объявления"><input value={filters.listing_type} onChange={(event) => updateFilter("listing_type", event.target.value)} placeholder="apartment" /></Field>
        <Field label="Request ID"><input value={filters.request_id} onChange={(event) => updateFilter("request_id", event.target.value)} placeholder="UUID запроса" /></Field>
        <Field label="Метод"><select value={filters.method} onChange={(event) => updateFilter("method", event.target.value)}><option value="">Все методы</option>{METHODS.map((item) => <option key={item} value={item}>{item}</option>)}</select></Field>
        <Field label="Аутентифицирован"><select value={filters.authenticated} onChange={(event) => updateFilter("authenticated", event.target.value)}><option value="">Все события</option><option value="true">Да</option><option value="false">Нет</option></select></Field>
        <Field label="С"><input type="datetime-local" value={filters.from} onChange={(event) => updateFilter("from", event.target.value)} /></Field>
        <Field label="По"><input type="datetime-local" value={filters.to} onChange={(event) => updateFilter("to", event.target.value)} /></Field>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="submit" className="inline-flex items-center gap-2 rounded-lg bg-[#8b2a5e] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#6e2048]"><Search size={16} />Применить</button>
        <button type="button" onClick={resetFilters} className="inline-flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-950"><RefreshCw size={16} />Сбросить</button>
        <button type="button" onClick={() => void exportCsv()} disabled={exportPending || !appliedFilters.from || !appliedFilters.to} title="Для экспорта укажите обе границы периода" className="inline-flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold text-[#8b2a5e] transition hover:bg-[#fff5fa] disabled:cursor-not-allowed disabled:text-slate-400"><Download size={16} />{exportPending ? "Экспорт…" : "CSV"}</button>
        {activeFiltersCount ? <span className="text-xs text-slate-500">Активных фильтров: {activeFiltersCount}</span> : null}
      </div>
    </form>

    {error ? <div className="mt-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{error}</div> : null}
    <div className="mt-5 overflow-hidden border-y border-slate-200 bg-white sm:rounded-2xl sm:border">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-4 sm:px-5"><div><p className="m-0 text-sm font-semibold text-slate-950">События</p><p className="m-0 mt-0.5 text-xs text-slate-500">Новые сверху · stack trace — в карточке</p></div><button type="button" onClick={() => setConfirmation({ type: "clear-all" })} disabled={actionPending} className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-50"><Trash2 size={16} />Очистить журнал</button></div>
      <div className="flex gap-1 overflow-x-auto border-b border-slate-200 px-4 pt-3 sm:px-5" role="tablist" aria-label="Статус просмотра событий">
        {(["all", "new", "reviewed"] as ReviewTab[]).map((tab) => {
          const title = tab === "all" ? "Все" : tab === "new" ? "Новые" : "Просмотренные";
          const active = reviewTab === tab;
          return <button key={tab} type="button" role="tab" aria-selected={active} onClick={() => setReviewTab(tab)} className={`whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-semibold transition ${active ? "border-[#8b2a5e] text-[#8b2a5e]" : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-900"}`}>{title}<span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-xs tabular-nums ${active ? "bg-[#fff0f7]" : "bg-slate-100 text-slate-600"}`}>{reviewCounts[tab]}</span></button>;
        })}
      </div>
      {selected.length ? <div className="flex flex-wrap items-center gap-2 border-b border-[#efd5e3] bg-[#fff8fc] px-4 py-3 sm:px-5"><span className="mr-2 text-sm font-semibold text-slate-900">Выбрано: {selected.length}</span><button type="button" disabled={actionPending} onClick={() => void updateReview(selected, true)} className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-semibold text-[#8b2a5e] hover:bg-white disabled:opacity-50"><Eye size={16} />Просмотрено</button><button type="button" disabled={actionPending} onClick={() => void updateReview(selected, false)} className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-semibold text-slate-700 hover:bg-white disabled:opacity-50"><EyeOff size={16} />Сделать новыми</button><button type="button" disabled={actionPending} onClick={() => setConfirmation({ type: "delete-selected" })} className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-semibold text-rose-700 hover:bg-white disabled:opacity-50"><Trash2 size={16} />Удалить</button><button type="button" onClick={() => setSelectedIds(new Set())} className="ml-auto rounded-lg p-1.5 text-slate-500 hover:bg-white" aria-label="Снять выделение"><X size={16} /></button></div> : null}
      {loading ? <div className="px-5 py-12 text-center text-sm text-slate-500">Загружаем журнал ошибок…</div> : null}
      {!loading && !events.length ? <div className="px-5 py-12 text-center text-sm text-slate-500">По этим фильтрам событий нет.</div> : null}
      {!loading && events.length ? <><div className="overflow-x-auto"><table className="min-w-[1100px] w-full text-left text-[13px]"><thead className="border-b border-slate-200 bg-slate-50 text-xs font-medium text-slate-500"><tr><th className="w-11 px-3 py-3"><input type="checkbox" checked={allVisibleSelected} onChange={toggleVisible} aria-label="Выбрать все видимые события" /></th><th className="px-4 py-3">Время</th><th className="px-3 py-3">Статус</th><th className="px-3 py-3">Ошибка</th><th className="px-3 py-3">Маршрут</th><th className="px-3 py-3">Пользователь</th><th className="px-3 py-3">Просмотр</th><th className="px-4 py-3">Request ID</th><th className="px-3 py-3"></th></tr></thead><tbody className="divide-y divide-slate-100">{events.map((item) => <tr key={item.id} className="cursor-pointer text-slate-700 transition hover:bg-[#fff8fc]" onClick={() => setSelectedId(item.id)}><td className="px-3 py-3.5" onClick={(event) => event.stopPropagation()}><input type="checkbox" checked={selectedIds.has(item.id)} onChange={() => toggleSelected(item.id)} aria-label={`Выбрать событие ${item.id}`} /></td><td className="whitespace-nowrap px-4 py-3.5 tabular-nums text-slate-500">{formatDate(item.occurred_at)}</td><td className="px-3 py-3.5"><span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-bold ${statusClass(item.status_code)}`}>{item.status_code}</span></td><td className="max-w-[240px] px-3 py-3.5"><p className="m-0 truncate font-mono text-xs font-semibold text-slate-800">{item.error_code}</p><p className="m-0 mt-1 truncate text-xs text-slate-500">{item.error_message}</p></td><td className="max-w-[250px] px-3 py-3.5 font-mono text-xs text-slate-700"><span className="font-bold">{item.method}</span> <span className="text-slate-500">{item.path}</span></td><td className="px-3 py-3.5">{item.user_id ? <Link className="font-medium text-[#8b2a5e] hover:text-[#6e2048]" onClick={(event) => event.stopPropagation()} to={`/control-panel/users/${encodeURIComponent(item.user_id)}`}>ID {item.user_id}</Link> : "—"}{item.user_email ? <p className="m-0 mt-1 max-w-[180px] truncate text-xs text-slate-500">{item.user_email}</p> : null}</td><td className="px-3 py-3.5"><ReviewBadge event={item} />{item.reviewed_at ? <p className="m-0 mt-1 whitespace-nowrap text-xs text-slate-500">{formatDate(item.reviewed_at)} · {item.reviewed_by || "—"}</p> : null}</td><td className="max-w-[180px] truncate px-4 py-3.5 font-mono text-xs text-slate-500">{item.request_id}</td><td className="px-3 py-3.5 text-slate-400"><ChevronRight size={18} /></td></tr>)}</tbody></table></div><div className="flex justify-center border-t border-slate-100 px-4 py-4">{nextCursor ? <button type="button" disabled={loadingMore} onClick={() => void loadEvents(nextCursor, appliedFilters, true)} className="rounded-lg px-4 py-2 text-sm font-semibold text-[#8b2a5e] transition hover:bg-[#fff5fa] disabled:cursor-wait disabled:text-slate-400">{loadingMore ? "Загружаем…" : "Показать ещё"}</button> : <span className="text-sm text-slate-400">Все события загружены</span>}</div></> : null}
    </div>
    {selectedId ? <DetailPanel event={selectedEvent} loading={detailLoading} error={detailError} pending={actionPending} onClose={() => setSelectedId(null)} onReview={(reviewed) => void updateReview([selectedId], reviewed)} onDelete={() => setConfirmation({ type: "delete-one", id: selectedId })} /> : null}
    {confirmation ? <ConfirmationDialog confirmation={confirmation} selectedCount={selected.length} pending={actionPending} onCancel={() => setConfirmation(null)} onConfirm={() => void confirmDeletion()} /> : null}
  </div>;
};
